import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DataTable from "../components/DataTable";
import Subheader from "../components/Subheader";
import Api from "../openapi";
import { DiscountResponse, DiscountResponseStateEnum } from "../openapi/client";
import { GridPaginationModel, GridCallbackDetails, GridSortModel, GridRenderCellParams } from "@mui/x-data-grid";
import { checkEmpty, checkIf, DiscountState, displayFormat, generateRandom } from "../utils";
import moment from "moment";
import useConfirm from "../hooks/useConfirm";
import { DeleteLabelIcon } from "../components/IconLabel";
import { useDispatch } from "react-redux";
import { setNotification } from "../store/notificationSlice";
import DiscountCrud from "../components/DiscountCrud";

export interface IEditDiscount {
  show: boolean;
  discount?: DiscountResponse;
}

export default function Discount() {
  const dispatch = useDispatch();

  const [discounts, setDiscounts] = useState<DiscountResponse[]>([]);
  const [createShow, setCreateShow] = useState(false);
  const [editShow, setEditShow] = useState<IEditDiscount>({ show: false, discount: undefined });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retriggerReq, setRetriggerReq] = useState<number>();

  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete a discount from the list, and this action is irreversible. Are you sure you want to proceed?",
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "code",
      sort: "asc",
    },
  ]);

  const handlePaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPaginationModel(model);
  };
  const handleSortChange = (sortModel: GridSortModel) => {
    sortModel.length && setSortModel(sortModel);
  };

  const create = () => {
    setCreateShow(true);
  };
  const edit = (params: any) => {
    setEditShow({ show: true, discount: params.row });
  };

  const deleteRec = async (params: any) => {
    const answer = await confirmDelete();
    if (!answer) return;

    try {
      const req = await Api.Discount.deleteDiscount(params.row.id);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Discount has been deleted successfully",
        })
      );
      setRetriggerReq(generateRandom());
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const handleClose = () => {
    setCreateShow(false);
    setEditShow({ show: false, discount: undefined });
  };
  const handleConfirm = () => {
    setRetriggerReq(generateRandom());
  };

  const columns = useMemo(() => {
    return [
      {
        field: "code",
        minWidth: 110,
        flex: 1,
        headerName: "Code",
        valueGetter: (params: any) => params.row.code,
      },
      {
        field: "percentage",
        minWidth: 110,
        flex: 0,
        headerName: "Percentage",
        valueGetter: (params: any) =>
          checkEmpty(params.row.percentage) ? "" : Math.round(params.row.percentage * 10e6) / 10e4 + " %",
      },
      {
        field: "amount",
        minWidth: 110,
        flex: 0,
        headerName: "Amount",
        valueGetter: (params: any) => params.row.amount,
      },
      {
        field: "minPurchaseAmount",
        minWidth: 110,
        flex: 0,
        headerName: "Min. Purchase Amount",
        valueGetter: (params: any) => params.row.minPurchaseAmount,
      },
      {
        field: "maxDiscountAmount",
        minWidth: 110,
        flex: 0,
        headerName: "Max. Discount Amount",
        valueGetter: (params: any) => params.row.maxDiscountAmount,
      },
      {
        field: "startDate",
        minWidth: 110,
        flex: 0,
        headerName: "Start Date",
        valueGetter: (params: any) =>
          checkIf(params.row.startDate) ? moment(params.row.startDate).format(displayFormat) : "",
      },
      {
        field: "endDate",
        minWidth: 110,
        flex: 0,
        headerName: "End Date",
        valueGetter: (params: any) =>
          checkIf(params.row.endDate) ? moment(params.row.endDate).format(displayFormat) : "",
      },
      {
        field: "active",
        flex: 0,
        minWidth: 110,
        headerName: "Status",
        sortable: false,
        hideable: false,
        valueGetter: (params: any) => DiscountState[params.row.state as DiscountResponseStateEnum],
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "normal",
              color: params.row.state === DiscountResponseStateEnum.Active ? "text.green" : "text.red",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "appliesToMealPrice",
        flex: 0,
        minWidth: 110,
        headerName: "Applies to Meal Price",
        sortable: false,
        hideable: false,
        valueGetter: (params: any) => (params.row.appliesToMealPrice ? "Yes" : "No"),
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "normal",
              color: params.row.appliesToMealPrice ? "text.green" : "text.red",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "appliesToDeliveryFee",
        flex: 0,
        minWidth: 110,
        headerName: "Applies to Delivery Fee",
        sortable: false,
        hideable: false,
        valueGetter: (params: any) => (params.row.appliesToDeliveryFee ? "Yes" : "No"),
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "normal",
              color: params.row.appliesToDeliveryFee ? "text.green" : "text.red",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "availableForAppSale",
        flex: 0,
        minWidth: 80,
        headerName: "Available for App Sale",
        sortable: false,
        hideable: false,
        valueGetter: (params: any) => (params.row.availableForAppSale ? "Yes" : "No"),
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "normal",
              color: params.row.availableForAppSale ? "text.green" : "text.red",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
    ];
  }, [discounts]);

  const getDiscounts = async () => {
    setLoading(true);
    try {
      const res = await Api.Discount.listDiscounts(
        paginationModel.page,
        paginationModel.pageSize,
        sortModel.map((i) => `${i.field},${i.sort}`)
      );

      setDiscounts(res.data.content as DiscountResponse[]);
      setTotalRowCount(Number(res.data.totalElements));
    } catch (error) {}
    setLoading(false);
  };

  useEffect(() => {
    getDiscounts();
  }, [paginationModel, sortModel, retriggerReq]);

  //   useEffect(() => {
  //     setTotalRowCount((prevRowCountState) =>
  //       pageInfo?.totalRowCount !== undefined
  //         ? pageInfo?.totalRowCount
  //         : prevRowCountState,
  //     );
  //   }, [pageInfo?.totalRowCount, setRowCountState]);

  return (
    <Box className="page-wrapper">
      <Subheader page="discount" />
      <Box p={6} className="page-container">
        <DataTable
          rows={discounts}
          rowCount={totalRowCount}
          columns={columns}
          sortingMode="server"
          sortModel={sortModel}
          paginationMode="server"
          paginationModel={paginationModel}
          paginationChange={handlePaginationChange}
          sortChange={handleSortChange}
          loading={loading}
          create={create}
          edit={edit}
          deleteRec={deleteRec}
          options={{
            entityName: "discount",
            permission: { create: "DISCOUNT_CREATE", edit: "DISCOUNT_EDIT", delete: "DISCOUNT_DELETE" },
            filter: false,
          }}
        />
      </Box>
      <DiscountCrud
        create
        open={createShow}
        title="Create New Discount"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <DiscountCrud
        edit
        open={editShow.show}
        discount={editShow.discount}
        title="Edit Discount"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <ConfirmDialog />
    </Box>
  );
}
